import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";

@Component({
    selector: "app-date-picker",
    templateUrl: "./date-picker.component.html",
    styleUrls: ["./date-picker.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatePickerComponent implements OnInit, OnChanges {
  @Input() label: string = "Date";
  @Input() control: AbstractControl | null = null;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() placeholder: string = "MM/DD/YYYY";
  @Input() dataId?: string;

  showCalendar = false;
  selectedMonth = new Date().getMonth();
  selectedYear = new Date().getFullYear();
  calendarDays: (Date | null)[] = [];

  months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  years: number[] = [];
  weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    // Ensure a FormControl is available even if the parent didn't bind one in tests
    if (!this.control) {
      this.control = new UntypedFormControl("");
    }
    this.setupValidators();
    this.generateYears();
    this.initializeCalendar();
    this.updateDisabledState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["required"]) {
      this.setupValidators();
    }
    if (changes["disabled"]) {
      this.updateDisabledState();
    }
    if (changes["disabled"] && this.control) {
      this.disabled ? this.control.disable() : this.control.enable();
    }
  }

  // Custom validator for date format and validity
  private dateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    // Check format
    const formatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!formatRegex.test(value)) {
      return { pattern: true };
    }

    // Check if date is valid
    const [mm, dd, yyyy] = value.split("/").map(Number);
    const date = new Date(yyyy, mm - 1, dd);

    if (
      date.getMonth() !== mm - 1 ||
      date.getDate() !== dd ||
      date.getFullYear() !== yyyy
    ) {
      return { invalidDate: true };
    }

    return null;
  }

  private setupValidators() {
    const validators = [this.dateValidator.bind(this)];
    if (this.required) {
      validators.push(Validators.required);
    }
    this.control.setValidators(validators);
    this.control.updateValueAndValidity();
  }

  private generateYears() {
    const currentYear = new Date().getFullYear();
    this.years = Array.from({ length: 100 }, (_, i) => currentYear - 50 + i);
  }

  private initializeCalendar() {
    // If control has a valid value, set calendar to that date
    if (this.control.value && !this.control.errors) {
      const [mm, dd, yyyy] = this.control.value.split("/").map(Number);
      this.selectedMonth = mm - 1;
      this.selectedYear = yyyy;
    }
    this.generateCalendarDays(this.selectedYear, this.selectedMonth);
  }

  private updateDisabledState() {
    if (!this.control) return;
    if (this.control.disabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  // Close calendar when clicking outside
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showCalendar = false;
    }
  }

  // Keyboard navigation
  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.showCalendar = false;
    }
  }

  toggleCalendar() {
    if (this.control.disabled) return;
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar) {
      this.initializeCalendar();
    }
  }

  generateCalendarDays(year: number, month: number) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    this.calendarDays = days;
  }

  selectDate(date: Date | null) {
    if (!date || this.control.disabled) return;

    const formatted = this.formatDate(date);
    this.control.setValue(formatted);
    this.control.markAsTouched();
    this.showCalendar = false;
  }

  formatDate(date: Date): string {
    const mm = ("0" + (date.getMonth() + 1)).slice(-2);
    const dd = ("0" + date.getDate()).slice(-2);
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  isToday(date: Date | null): boolean {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  isSelected(date: Date | null): boolean {
    if (!date || !this.control.value || this.control.errors) return false;
    return this.control.value === this.formatDate(date);
  }

  onInputBlur() {
    const value = this.control.value;
    if (!value) return;

    // Trigger validation
    this.control.updateValueAndValidity();

    // If valid, update calendar view
    if (!this.control.errors) {
      const [mm, dd, yyyy] = value.split("/").map(Number);
      this.selectedMonth = mm - 1;
      this.selectedYear = yyyy;
      this.generateCalendarDays(this.selectedYear, this.selectedMonth);
    }
  }

  onMonthChange(monthIndex: string) {
    this.selectedMonth = parseInt(monthIndex);
    this.generateCalendarDays(this.selectedYear, this.selectedMonth);
  }

  onYearChange(year: string) {
    this.selectedYear = parseInt(year);
    this.generateCalendarDays(this.selectedYear, this.selectedMonth);
  }

  changeMonth(delta: number) {
    let newMonth = this.selectedMonth + delta;
    let newYear = this.selectedYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    this.selectedMonth = newMonth;
    this.selectedYear = newYear;
    this.generateCalendarDays(this.selectedYear, this.selectedMonth);
  }

  // Helper method to get error message
  getErrorMessage(): string {
    if (this.control.errors && this.control.errors["required"]) {
      return "This field is required.";
    }
    if (this.control.errors && this.control.errors["pattern"]) {
      return "Please enter a valid date format.";
    }
    if (this.control.errors && this.control.errors["invalidDate"]) {
      return "Please enter a valid date.";
    }
    return "";
  }
}
